/**
 * Hook for fetching and computing behavioral reading insights
 */

import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { 
  calculateBehavioralInsights, 
  type UserBookWithDetails,
  type BehavioralInsightsData 
} from '@/lib/behavioralInsightsUtils';
import { generateAllInsights, type GeneratedInsight } from '@/lib/insightNarrativeUtils';

interface UseBehavioralInsightsResult {
  insights: GeneratedInsight[];
  rawData: BehavioralInsightsData | null;
  isLoading: boolean;
  error: string | null;
  hasEnoughData: boolean;
}

export function useBehavioralInsights(year?: number): UseBehavioralInsightsResult {
  const { user } = useAuth();
  const [completedBooks, setCompletedBooks] = useState<UserBookWithDetails[]>([]);
  const [dnfBooks, setDnfBooks] = useState<UserBookWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchBooks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch completed books
        let completedQuery = supabase
          .from('user_books')
          .select(`
            id,
            book_id,
            started_at,
            finished_at,
            rating_star,
            shelf,
            reading_format,
            reading_progress_percent,
            books!user_books_book_id_fkey (
              id,
              title,
              authors,
              page_count,
              subjects
            )
          `)
          .eq('user_id', user.id)
          .eq('shelf', 'read');

        // Filter by year if specified
        if (year) {
          const startOfYear = `${year}-01-01`;
          const endOfYear = `${year}-12-31`;
          completedQuery = completedQuery
            .gte('finished_at', startOfYear)
            .lte('finished_at', endOfYear);
        }

        const { data: completedData, error: completedError } = await completedQuery;

        if (completedError) throw completedError;

        // Fetch DNF books
        let dnfQuery = supabase
          .from('user_books')
          .select(`
            id,
            book_id,
            started_at,
            finished_at,
            rating_star,
            shelf,
            reading_format,
            reading_progress_percent,
            books!user_books_book_id_fkey (
              id,
              title,
              authors,
              page_count,
              subjects
            )
          `)
          .eq('user_id', user.id)
          .eq('shelf', 'unfinished');

        const { data: dnfData, error: dnfError } = await dnfQuery;

        if (dnfError) throw dnfError;

        // Filter out books without book details and cast to our type
        const validCompleted = (completedData || [])
          .filter(b => b.books !== null)
          .map(b => ({
            ...b,
            reading_format: b.reading_format as 'print' | 'digital' | 'audio' | null | undefined,
            books: b.books!
          })) as UserBookWithDetails[];
          
        const validDnf = (dnfData || [])
          .filter(b => b.books !== null)
          .map(b => ({
            ...b,
            reading_format: b.reading_format as 'print' | 'digital' | 'audio' | null | undefined,
            books: b.books!
          })) as UserBookWithDetails[];

        setCompletedBooks(validCompleted);
        setDnfBooks(validDnf);
      } catch (err) {
        console.error('Error fetching books for insights:', err);
        setError('Failed to load reading data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBooks();
  }, [user, year]);

  // Calculate insights from the data
  const { rawData, insights, hasEnoughData } = useMemo(() => {
    if (completedBooks.length < 5) {
      return {
        rawData: null,
        insights: [],
        hasEnoughData: false
      };
    }

    const behavioralData = calculateBehavioralInsights(completedBooks, dnfBooks);
    const generatedInsights = generateAllInsights(behavioralData);

    return {
      rawData: behavioralData,
      insights: generatedInsights,
      hasEnoughData: generatedInsights.length > 0
    };
  }, [completedBooks, dnfBooks]);

  return {
    insights,
    rawData,
    isLoading,
    error,
    hasEnoughData
  };
}
